import React from 'react';
import { useAuth } from '../../context/AuthContext';

interface PromotionPreviewProps {
  title: string;
  description: string;
  discount: number;
  imageUrl: string;
}

const PromotionPreview: React.FC<PromotionPreviewProps> = ({
  title,
  description,
  discount,
  imageUrl
}) => {
  const { userInfo } = useAuth();

  // Obtener nombre de la empresa
  const getCompanyName = (): string => {
    if (userInfo && (userInfo as any).companyName) {
      return (userInfo as any).companyName;
    }
    return 'Tu Empresa';
  };

  return (
    <div className="bg-xervix-bg-tertiary dark:bg-xervix-bg-tertiary-dark rounded-lg p-6">
      <h3 className="text-lg font-semibold text-xervix-text-primary dark:text-xervix-text-primary-dark mb-6">
        Vista Previa de la Promoción
      </h3>

      {/* Preview Card */}
      <div className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-black rounded-2xl overflow-hidden shadow-2xl max-w-md mx-auto">
        {/* Background Image */}
        <div className="relative h-64">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt="Promoción"
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-gray-700 to-gray-900 flex items-center justify-center">
              <div className="text-gray-400 text-center">
                <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <p className="text-sm">Imagen de la promoción</p>
              </div>
            </div>
          )}
          
          {/* Overlay gradient */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
          
          {/* Discount Badge */}
          {discount > 0 && (
            <div className="absolute top-4 right-4">
              <div className="bg-xervix-primary text-white px-4 py-2 rounded-full shadow-lg transform rotate-12">
                <span className="text-2xl font-bold">{discount}%</span>
                <span className="text-sm block text-center">OFF</span>
              </div>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
          {/* Company Name */}
          <div className="mb-2">
            <span className="text-xs uppercase tracking-wide text-gray-300 font-medium">
              {getCompanyName()}
            </span>
          </div>

          {/* Title */}
          <h2 className="text-xl font-bold mb-2 leading-tight">
            {title || 'Título de tu promoción'}
          </h2>

          {/* Description */}
          <p className="text-sm text-gray-200 leading-relaxed mb-4 line-clamp-3">
            {description || 'Descripción de tu promoción aparecerá aquí...'}
          </p>

          {/* CTA Button */}
          <div className="flex items-center justify-between">
            <button className="bg-white text-gray-900 px-6 py-2 rounded-full font-semibold text-sm hover:bg-gray-100 transition-colors shadow-lg">
              ¡Aprovecha ahora!
            </button>
            
            {/* Logo placeholder */}
            <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
              <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
              </svg>
            </div>
          </div>
        </div>

        {/* Decorative elements */}
        <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
          {/* Sparkle effects */}
          <div className="absolute top-8 left-8 w-2 h-2 bg-white rounded-full opacity-60 animate-pulse"></div>
          <div className="absolute top-16 right-16 w-1 h-1 bg-white rounded-full opacity-40 animate-pulse delay-1000"></div>
          <div className="absolute bottom-20 left-12 w-1.5 h-1.5 bg-white rounded-full opacity-50 animate-pulse delay-500"></div>
        </div>
      </div>

      {/* Preview Info */}
      <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
        <div className="flex items-start space-x-3">
          <svg className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <h4 className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-1">
              Vista Previa
            </h4>
            <p className="text-xs text-blue-600 dark:text-blue-300">
              Así se verá tu promoción para los usuarios. Los cambios se reflejan en tiempo real mientras completas el formulario.
            </p>
          </div>
        </div>
      </div>

      {/* Empty state message */}
      {!title && !description && !imageUrl && discount === 0 && (
        <div className="mt-4 text-center">
          <p className="text-sm text-xervix-text-secondary dark:text-xervix-text-secondary-dark">
            Completa el formulario para ver la vista previa de tu promoción
          </p>
        </div>
      )}
    </div>
  );
};

export default PromotionPreview;
